import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface ProfileContainerState {
    profileData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useProfileContainer = (state: ProfileContainerState = {}) => {
    console.log('ProfileContainer');
    const { api } = getUrls();

    const [profileData, setProfileData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const profileUuid = search.get('profile_uuid') || window.localStorage.getItem('profile_uuid');

    const [getProfileStatus, setGetProfileStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { profileId: profileIdV1 } = useParams<{ profileId?: string }>();
    const { profile_id: profileIdV2 } = getUrlParams();
    const profileId = profileIdV1 || profileIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getProfileDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                profile_uuid: profileUuid!,
                profile_id: profileId,
                auth_mode: authMode
            })
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [profileUuid, profileId, authMode]);

    const getProfile = useCallback(async () => {
        setGetProfileStatus('LOADING');

        try {
            const data = state.profileData ?? (await getProfileDataCall());

            setProfileData(data);
            setGetProfileStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetProfileStatus('ERROR');
        }
    }, [state.profileData, getProfileDataCall, returnErrorMessage]);

    useEffect(() => {
        async function fetchData() {
            if (profileId || profileUuid) {
                await getProfile();
                setProfileData({
                    loading: false
                });
                setGetProfileStatus('SUCCESS');
            }
        }
        fetchData();
    }, [profileId, setProfileData, profileUuid, setGetProfileStatus, getProfile]);

    return {
        profileId,
        api,
        profileData,
        getProfileStatus,

        errorMessage
    };
};

const ProfileContainer = createContainer(useProfileContainer);

export default ProfileContainer;
